import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';

import { FBoxContainer, FHSkeletonLoader } from '../../../base';
import { formatClientBayNames } from './utils';
import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR } from '../../../../utils/colors';
import ExclamationIcon from '../../../svgs/ExclamationIcon';

import type {
  IClientAssignment,
  IClientFacilityMap,
  IOption,
  IPriorAuthorizationDetail,
  IServiceCode,
} from '../../../../utils/types';

interface ClaimBuilderPAAndPolicyDetailsProps {
  selectedClient: IOption | null;
  paDetailsLoading: boolean;
  clientDeatilsLoading: boolean;
  selectedProcedureCode: IServiceCode | null;
  unitsLeft: number | undefined;
  weeklyCohortAssignment: IClientAssignment[];
  billingFacility: IClientFacilityMap | undefined;
  serviceLocation: IClientFacilityMap | undefined;
  selectedPolicyPriorAuthorizationDetails: IPriorAuthorizationDetail | null;
  selectedPolicy: IOption | null;
  modifierLabel: string;
}

const ClaimBuilderPAAndPolicyDetails = ({
  paDetailsLoading,
  clientDeatilsLoading,
  selectedClient,
  selectedProcedureCode,
  weeklyCohortAssignment,
  billingFacility,
  serviceLocation,
  unitsLeft,
  selectedPolicyPriorAuthorizationDetails,
  selectedPolicy,
  modifierLabel,
}: ClaimBuilderPAAndPolicyDetailsProps) => {
  const clientBayNames = useMemo(() => {
    return weeklyCohortAssignment?.length > 0
      ? formatClientBayNames(weeklyCohortAssignment)
      : 'Cannot determine';
  }, [weeklyCohortAssignment]);
  return (
    <FBoxContainer contentStyle={{ p: 2, minHeight: '200px' }}>
      {selectedClient && selectedProcedureCode ? (
        <Box display="flex" gap={2} height="170px" justifyContent="space-between">
          <Box sx={{ backgroundColor: FORTA_BLUE.lightest, borderRadius: '16px', p: 2, flex: 1 }}>
            {clientDeatilsLoading ? (
              <FHSkeletonLoader
                rowCount={3}
                height={20}
                sx={{ mt: 2 }}
                containerStyles={{ mt: 1 }}
                width={'80%'}
              />
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>
                    Client&apos;s bay
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: weeklyCohortAssignment?.length > 0 ? FORTA_BLACK : FORTA_ERROR,
                    }}
                  >
                    {clientBayNames}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>
                    Billing facility
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {billingFacility?.facility?.label ?? '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>
                    Service location
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {serviceLocation?.facility?.label ?? '--'}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: FORTA_BLUE.lightest,
              borderRadius: '16px',
              p: 2,
              width: '27%',
            }}
          >
            {paDetailsLoading || clientDeatilsLoading ? (
              <FHSkeletonLoader
                rowCount={4}
                height={20}
                sx={{ mt: 2 }}
                containerStyles={{ mt: 1 }}
                width={'80%'}
              />
            ) : (
              <>
                {' '}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>PA</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {selectedPolicyPriorAuthorizationDetails?.prior_authorization_number_issued ??
                      '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>PA start</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {selectedPolicyPriorAuthorizationDetails?.date_of_service_start_approved ??
                      '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>PA end</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {selectedPolicyPriorAuthorizationDetails?.date_of_service_end_approved ?? '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>Policy</Typography>
                  <Typography sx={{ fontSize: 12 }}>{selectedPolicy?.label ?? '--'}</Typography>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: FORTA_BLUE.lightest,
              borderRadius: '16px',
              p: 2,
              width: '27%',
            }}
          >
            {paDetailsLoading || clientDeatilsLoading ? (
              <FHSkeletonLoader
                rowCount={4}
                height={20}
                sx={{ mt: 2 }}
                containerStyles={{ mt: 1 }}
                width={'80%'}
              />
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>PA bay</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {selectedPolicyPriorAuthorizationDetails?.submission_bay ?? '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>Modifier</Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {modifierLabel ? modifierLabel : '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>
                    Units approved
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    {selectedPolicyPriorAuthorizationDetails?.approved_units ?? '--'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ mr: 1, fontSize: 12, fontWeight: 700 }}>Units left</Typography>
                  <Typography sx={{ fontSize: 12 }}>{unitsLeft ?? '--'}</Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ pt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          data-testid="claimDetailsMessageContainer"
        >
          <ExclamationIcon />
          <Typography variant="body1" sx={{ ml: 2, fontWeight: '700', color: FORTA_BLUE.light1 }}>
            Select client and procedure code to build claims.
          </Typography>
        </Box>
      )}
    </FBoxContainer>
  );
};

export default ClaimBuilderPAAndPolicyDetails;
